// What the dashboard shows, worked out from the summaries the server already returns. Pure functions, no React.
// A section that could not be loaded is null in `stats`; its numbers are then null (shown as "—"), never 0 and never invented.

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const has = (section) => section !== null && section !== undefined;

/** Percentage of `part` in `part + rest`, whole number; null when there is nothing to measure. */
export const share = (part, rest) => {
  const total = num(part) + num(rest);
  return total > 0 ? Math.round((num(part) / total) * 100) : null;
};

/** Share with one decimal (for rates such as rejections). */
export const rate = (part, total) => (num(total) > 0 ? Math.round((num(part) / num(total)) * 1000) / 10 : null);

export const rupees = (v) => `₹${num(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const pick = (section, key) => (has(section) ? num(section[key]) : null);
const sum = (...values) => (values.some((v) => v === null) ? null : values.reduce((a, b) => a + b, 0));

/** The sections of `stats` that matter to everyone and could not be loaded. */
export const unavailableSections = (stats, { includeSecurity = false } = {}) => {
  const wanted = [
    ['phlebotomy', 'Sample collection'], ['accession', 'Sample receiving'], ['pathology', 'Pathology'], ['approval', 'Report approval'],
    ['radiology', 'Radiology'], ['settlement', 'Billing'], ['outsource', 'Outsource'], ['recentRegistrations', 'Recent registrations'],
  ];
  if (includeSecurity) wanted.push(['security', 'Access summary']);
  return wanted.filter(([key]) => !has(stats?.[key])).map(([, label]) => label);
};

// ── Date range ───────────────────────────────────────────────────────────────────────────────────

export const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

export const MAX_RANGE_DAYS = 92;

export const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayOf = (iso) => new Date(`${iso}T00:00:00`);

/** The from / to dates (yyyy-MM-dd, local) of a preset that ends today or yesterday. `custom` keeps the dates it is given. */
export const rangeFor = (key, today = new Date(), custom = null) => {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const back = (n) => new Date(t.getFullYear(), t.getMonth(), t.getDate() - n);
  if (key === 'yesterday') return { key, from: isoDay(back(1)), to: isoDay(back(1)) };
  if (key === '7d') return { key, from: isoDay(back(6)), to: isoDay(t) };
  if (key === '30d') return { key, from: isoDay(back(29)), to: isoDay(t) };
  if (key === 'month') return { key, from: isoDay(new Date(t.getFullYear(), t.getMonth(), 1)), to: isoDay(t) };
  if (key === 'custom' && custom) return { key, from: custom.from, to: custom.to };
  return { key: 'today', from: isoDay(t), to: isoDay(t) };
};

/** Null when the dates can be used; otherwise the sentence to show. */
export const validateRange = (from, to, today = new Date()) => {
  if (!from || !to) return 'Choose both dates.';
  if (dayOf(from) > dayOf(to)) return 'The end date cannot be before the start date.';
  if (dayOf(to) > new Date(today.getFullYear(), today.getMonth(), today.getDate())) return 'The end date cannot be in the future.';
  if ((dayOf(to) - dayOf(from)) / 86400000 >= MAX_RANGE_DAYS) return `Choose a period of up to ${MAX_RANGE_DAYS} days.`;
  return null;
};

export const isToday = (range, today = new Date()) => range?.from === isoDay(today) && range?.to === isoDay(today);
export const dayCount = (range) => (range ? Math.round((dayOf(range.to) - dayOf(range.from)) / 86400000) + 1 : 0);

/** "27 Oct 2026" or "21 Oct – 27 Oct 2026". */
export const rangeLabel = (range) => {
  if (!range) return '';
  const fmt = (iso, withYear) => dayOf(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
  return range.from === range.to ? fmt(range.to, true) : `${fmt(range.from, false)} – ${fmt(range.to, true)}`;
};

/**
 * What happened in the period. From the server's period summary; when that could not be loaded and the period is today, the numbers of
 * today that the older summaries carry are used instead (what is not in them stays null).
 */
export const periodNumbers = (stats, period, today) => {
  if (has(period)) return period;
  if (!today) return null;
  const ph = stats?.phlebotomy; const ac = stats?.accession; const pa = stats?.pathology; const ap = stats?.approval;
  if (!has(ph) && !has(ac) && !has(pa) && !has(ap)) return null;
  return {
    registered: pick(ph, 'totalPatientsToday'), billed: null, amountReceived: null, testsOrdered: null, collected: pick(ph, 'collectedTodayCount'),
    received: pick(ac, 'receivedTodayCount'), rejected: pick(ac, 'rejectedTodayCount'), resultsEntered: null, approved: pick(ap, 'totalApprovedToday'),
    critical: pick(pa, 'criticalTodayCount'), daily: [],
  };
};

/** Eight numbers that follow the chosen period. */
export const periodKpis = (stats, period, today = true) => {
  const p = periodNumbers(stats, period, today);
  const v = (key) => (p && p[key] !== undefined && p[key] !== null ? num(p[key]) : null);
  const none = p === null;
  const critical = v('critical');
  return [
    { key: 'registered', label: 'Registered', value: v('registered'), hint: v('testsOrdered') !== null ? `${v('testsOrdered')} tests ordered` : 'Bills registered', tone: 'info', screen: 'REGISTRATION', route: '/registration', icon: 'users' },
    { key: 'billed', label: 'Billed', value: v('billed') !== null ? rupees(v('billed')) : null, hint: v('amountReceived') !== null ? `${rupees(v('amountReceived'))} received` : 'Not available', tone: 'purple', screen: 'SETTLEMENT', route: '/settlement', icon: 'rupee' },
    { key: 'collected', label: 'Collected', value: v('collected'), hint: 'Tests collected', tone: 'success', screen: 'PHLEBOTOMY', route: '/phlebotomy', icon: 'drop' },
    { key: 'received', label: 'Received at Lab', value: v('received'), hint: 'Tests received', tone: 'teal', screen: 'SAMPLE_RECEIVE', route: '/sample-receive', icon: 'inbox' },
    { key: 'entered', label: 'Results Entered', value: v('resultsEntered'), hint: v('resultsEntered') === null ? 'Not available' : 'Tests with results', tone: 'info', screen: 'PATHOLOGY_RESULTS', route: '/pathology-results', icon: 'flask' },
    { key: 'approved', label: 'Approved', value: v('approved'), hint: 'Reports released', tone: 'success', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue', icon: 'check' },
    {
      key: 'critical', label: 'Critical Results', value: critical, tone: critical > 0 ? 'danger' : 'default', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue', routeParams: { status: 'Critical' },
      icon: 'alert', hint: none || critical === null ? 'Not available' : (critical > 0 ? 'Flagged in this period' : 'None flagged'),
    },
    { key: 'rejected', label: 'Rejected', value: v('rejected'), hint: 'Specimens rejected', tone: v('rejected') > 0 ? 'warning' : 'default', screen: 'SAMPLE_RECEIVE', route: '/sample-receive', icon: 'alert' },
  ];
};

/** Four numbers that are what is open right now, whatever period is chosen. */
export const nowKpis = (stats) => {
  const ph = stats?.phlebotomy; const pa = stats?.pathology; const ap = stats?.approval; const st = stats?.settlement;
  return [
    { key: 'await-collect', label: 'Awaiting Collection', value: pick(ph, 'pendingCount'), tone: pick(ph, 'pendingCount') > 0 ? 'warning' : 'default', screen: 'PHLEBOTOMY', route: '/phlebotomy', icon: 'drop', hint: 'Registered, sample not drawn' },
    { key: 'entry', label: 'Results Pending Entry', value: pick(pa, 'pendingCount'), tone: pick(pa, 'pendingCount') > 0 ? 'warning' : 'default', screen: 'PATHOLOGY_RESULTS', route: '/pathology-results', icon: 'flask', hint: has(pa) ? `${pick(pa, 'draftCount')} saved as draft` : 'Not available' },
    { key: 'signoff', label: 'Awaiting Sign-off', value: pick(ap, 'totalPending'), tone: pick(ap, 'totalPending') > 0 ? 'warning' : 'default', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue', icon: 'stamp', hint: has(ap) ? `${pick(ap, 'totalHeld')} on hold` : 'Not available' },
    { key: 'dues', label: 'Outstanding Dues', value: has(st) ? rupees(pick(st, 'totalDueAmount')) : null, tone: pick(st, 'totalDueAmount') > 0 ? 'purple' : 'default', screen: 'SETTLEMENT', route: '/settlement', icon: 'rupee', hint: has(st) ? `${pick(st, 'unpaidCount')} unpaid orders` : 'Not available' },
  ];
};

/** The patient's path through the lab: what is waiting at each step now, and how much was done in the period. */
export const pipelineStages = (stats, period, today = true) => {
  const ph = stats?.phlebotomy; const ac = stats?.accession; const pa = stats?.pathology; const ap = stats?.approval;
  const p = periodNumbers(stats, period, today);
  const done = (key) => (p && p[key] !== undefined && p[key] !== null ? num(p[key]) : null);
  const stage = (key, label, sub, waiting, doneValue, doneLabel, waitingLabel, screen, route) => ({
    key, label, sub, waiting, done: doneValue, doneLabel, waitingLabel, screen, route, percent: waiting === null || doneValue === null ? null : share(doneValue, waiting),
  });
  return [
    { key: 'registration', label: 'Registration', sub: 'Patient intake', waiting: null, done: done('registered'), doneLabel: 'Registered', waitingLabel: '', screen: 'REGISTRATION', route: '/registration', percent: null },
    stage('collection', 'Collection', 'Sample draw', pick(ph, 'pendingCount'), done('collected'), 'Collected', 'Waiting now', 'PHLEBOTOMY', '/phlebotomy'),
    stage('receiving', 'Receiving', 'Lab accession', pick(ac, 'pendingReceiveCount'), done('received'), 'Received', 'In transit now', 'SAMPLE_RECEIVE', '/sample-receive'),
    stage('entry', 'Result Entry', 'Testing', pick(pa, 'pendingCount'), done('resultsEntered') ?? pick(pa, 'draftCount'), done('resultsEntered') !== null ? 'Entered' : 'Drafts saved', 'Pending now', 'PATHOLOGY_RESULTS', '/pathology-results'),
    stage('approval', 'Approval', 'Doctor sign-off', pick(ap, 'totalPending'), done('approved'), 'Approved', 'Awaiting now', 'REPORT_APPROVAL', '/reports/approval-queue'),
  ];
};

/** One bar group per day: how many bills, collections and approvals. Empty when the server sent no days. */
export const trendDays = (period) => {
  const days = Array.isArray(period?.daily) ? period.daily : [];
  const top = Math.max(1, ...days.map((d) => Math.max(num(d.registered), num(d.collected), num(d.approved))));
  return days.map((d) => ({
    date: String(d.date).slice(0, 10), registered: num(d.registered), collected: num(d.collected), approved: num(d.approved),
    heights: { registered: Math.round((num(d.registered) / top) * 100), collected: Math.round((num(d.collected) / top) * 100), approved: Math.round((num(d.approved) / top) * 100) },
  }));
};

const SEVERITY_RANK = { critical: 0, high: 1, warning: 2, info: 3 };

/** Things that need someone now. Only non-zero ones; the most serious first. */
export const attentionItems = (stats) => {
  const pa = stats?.pathology; const ap = stats?.approval; const ac = stats?.accession; const ph = stats?.phlebotomy; const st = stats?.settlement; const ou = stats?.outsource;
  const critical = (pick(pa, 'criticalTodayCount') || 0) + (pick(ap, 'totalCritical') || 0);
  const items = [
    { key: 'critical', severity: 'critical', label: 'Critical result flags', count: critical, detail: 'Results beyond panic limits', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue', routeParams: { status: 'Critical' } },
    { key: 'overdue', severity: 'high', label: 'Overdue outsource samples', count: pick(ou, 'overdueCount'), detail: 'Reference lab past its due time', screen: 'OUTSOURCE', route: '/outsource/worklist' },
    { key: 'rejected', severity: 'high', label: 'Specimens rejected today', count: pick(ac, 'rejectedTodayCount'), detail: 'Recollection may be needed', screen: 'SAMPLE_RECEIVE', route: '/sample-receive' },
    { key: 'held', severity: 'warning', label: 'Reports on hold', count: pick(ap, 'totalHeld'), detail: 'Held by a pathologist', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue', routeParams: { status: 'Held' } },
    { key: 'amend', severity: 'warning', label: 'Amendment requests', count: pick(ap, 'totalAmendmentRequests'), detail: 'Released reports to be corrected', screen: 'REPORT_APPROVAL', route: '/reports/approval-queue' },
    { key: 'transit', severity: 'warning', label: 'Specimens not yet received', count: pick(ac, 'pendingReceiveCount'), detail: 'Collected but not at the lab', screen: 'SAMPLE_RECEIVE', route: '/sample-receive' },
    { key: 'dues', severity: 'warning', label: 'Unpaid patient bills', count: pick(st, 'unpaidCount'), detail: has(st) ? `${rupees(pick(st, 'totalDueAmount'))} to collect` : '', screen: 'SETTLEMENT', route: '/settlement' },
    { key: 'collect', severity: 'info', label: 'Patients awaiting collection', count: pick(ph, 'pendingCount'), detail: 'Registered, sample not drawn', screen: 'PHLEBOTOMY', route: '/phlebotomy' },
  ];
  return items.filter((i) => i.count > 0).sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.count - a.count);
};

/** The four department cards. Each `rows` entry is one number; `progress` (0-100) is shown as a bar when it can be worked out. */
export const departmentCards = (stats) => {
  const pa = stats?.pathology; const ra = stats?.radiology; const ou = stats?.outsource; const st = stats?.settlement;
  return [
    {
      key: 'pathology', title: 'Pathology', screen: 'PATHOLOGY_RESULTS', route: '/pathology-results', available: has(pa),
      rows: [
        { label: 'Pending entry', value: pick(pa, 'pendingCount'), tone: 'warning' }, { label: 'Drafts', value: pick(pa, 'draftCount') },
        { label: 'On hold', value: pick(pa, 'holdCount'), tone: 'warning' }, { label: 'Critical today', value: pick(pa, 'criticalTodayCount'), tone: 'danger' },
        { label: 'Approved today', value: pick(pa, 'approvedTodayCount'), tone: 'success' }, { label: 'Eligible today', value: pick(pa, 'totalEligibleToday') },
      ],
      progress: has(pa) ? share(pick(pa, 'approvedTodayCount'), pick(pa, 'pendingCount') + pick(pa, 'draftCount')) : null, progressLabel: 'of today\'s work approved',
    },
    {
      key: 'radiology', title: 'Radiology', screen: 'RADIOLOGY_RESULTS', route: '/radiology-results', available: has(ra),
      rows: [
        { label: 'Studies pending', value: pick(ra, 'pendingResults'), tone: 'warning' }, { label: 'Draft reports', value: pick(ra, 'drafts') },
        { label: 'Completed today', value: pick(ra, 'approvedToday'), tone: 'success' }, { label: 'Studies today', value: pick(ra, 'totalToday') },
      ],
      progress: has(ra) ? share(pick(ra, 'approvedToday'), pick(ra, 'pendingResults') + pick(ra, 'drafts')) : null, progressLabel: 'completed',
    },
    {
      key: 'outsource', title: 'Outsource', screen: 'OUTSOURCE', route: '/outsource/worklist', available: has(ou),
      rows: [
        { label: 'Total sent out', value: pick(ou, 'totalOutsourced') }, { label: 'Ready to dispatch', value: pick(ou, 'readyForDispatchCount'), tone: 'warning' },
        { label: 'Dispatched', value: pick(ou, 'dispatchedCount') }, { label: 'At reference lab', value: pick(ou, 'atLabCount') },
        { label: 'Result received', value: pick(ou, 'resultReceivedCount') }, { label: 'Verified', value: pick(ou, 'verifiedCount'), tone: 'success' },
        { label: 'Overdue', value: pick(ou, 'overdueCount'), tone: 'danger' },
      ],
      progress: has(ou) ? share(pick(ou, 'verifiedCount'), pick(ou, 'totalOutsourced') - pick(ou, 'verifiedCount')) : null, progressLabel: 'verified',
    },
    {
      key: 'billing', title: 'Billing & Dues', screen: 'SETTLEMENT', route: '/settlement', available: has(st),
      rows: [
        { label: 'Outstanding', value: has(st) ? rupees(pick(st, 'totalDueAmount')) : null, tone: 'purple' }, { label: 'Unpaid orders', value: pick(st, 'unpaidCount'), tone: 'warning' },
        { label: 'On credit', value: pick(st, 'creditCount') }, { label: 'Fully paid', value: pick(st, 'fullPaidCount'), tone: 'success' },
        { label: 'Bills in view', value: pick(st, 'totalBillsCount') },
      ],
      progress: has(st) ? share(pick(st, 'fullPaidCount'), pick(st, 'unpaidCount') + pick(st, 'creditCount')) : null, progressLabel: 'fully paid',
    },
  ];
};

/** One line of the recent registrations table, whatever shape the list endpoint used. */
export const registrationRow = (item) => {
  const due = num(item.balanceDue ?? item.dueAmount);
  return {
    id: item.ledgerTransactionId || item.id || item.ledgerTransactionNo || item.labNo || item.labno,
    labNo: item.ledgerTransactionNo || item.labNo || item.labno || '',
    patient: item.patientName || item.pname || 'Patient',
    ageGender: item.ageGender || `${item.age ? `${item.age} ${item.ageType || 'Y'}` : '-'} / ${item.gender || '-'}`,
    billed: num(item.netAmount ?? item.grossAmount),
    due,
    collected: Boolean(item.isSampleCollected || item.isCollected),
  };
};

/** Good morning / afternoon / evening. */
export const greeting = (date = new Date()) => {
  const h = date.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

// ── Staff activity ────────────────────────────────────────────────────────────────────────────────

/** The six things a person can do, in the order of a sample's journey. */
export const ACTIVITY_MEASURES = [
  { key: 'registered', short: 'Reg', label: 'Registered', hint: 'Bills registered' },
  { key: 'collected', short: 'Coll', label: 'Collected', hint: 'Tests collected' },
  { key: 'received', short: 'Recv', label: 'Received', hint: 'Tests received at the lab' },
  { key: 'resultsEntered', short: 'Res', label: 'Results', hint: 'Tests with results entered' },
  { key: 'approved', short: 'Appr', label: 'Approved', hint: 'Tests approved' },
  { key: 'rejected', short: 'Rej', label: 'Rejected', hint: 'Specimens rejected' },
];

export const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

/**
 * Shapes the server's answer for the panel. For a person who sees only themselves: their own six numbers. For a manager: each employee with
 * a bar relative to the busiest, the non-zero numbers, and the totals.
 */
export const activityView = (data, limit = 8) => {
  if (!data) return null;
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const total = (r) => ACTIVITY_MEASURES.reduce((a, m) => a + num(r[m.key]), 0);
  if (!data.showsEveryone) {
    const me = rows[0] || {};
    return { everyone: false, name: me.employeeName || '', measures: ACTIVITY_MEASURES.map((m) => ({ ...m, value: num(me[m.key]) })), total: total(me) };
  }
  const busiest = Math.max(1, ...rows.map(total));
  const people = rows.map((r) => ({
    id: r.employeeId, name: r.employeeName, designation: r.designation || '', total: total(r), percent: Math.round((total(r) / busiest) * 100),
    parts: ACTIVITY_MEASURES.filter((m) => num(r[m.key]) > 0).map((m) => ({ key: m.key, short: m.short, hint: m.hint, value: num(r[m.key]) })),
  }));
  return {
    everyone: true,
    people: people.slice(0, limit),
    hidden: Math.max(0, people.length - limit),
    count: people.length,
    total: data.totals ? total(data.totals) : people.reduce((a, p) => a + p.total, 0),
  };
};
